/**
 * Sincronización (docs/esquema.md §5.2, lado cliente, endurecida por la ronda
 * del plan): single-flight con Web Lock, lotes de hasta 25 de UN epoch por
 * request, respuesta VALIDADA antes de tocar nada, ACKs en una transacción,
 * desvío de reloj medido contra hora_servidor en el punto medio del request.
 * 401 congela (desenrolado); 400, 411 y 413 son un bug de cliente visible; red/5xx
 * deja que el planificador reintente con backoff.
 *
 * v3: sin fotos. La respuesta trae `estacion` (la vigente del dispositivo) y
 * `raiz_id` en los acuses aplicados, para los recibos por raíz.
 */

import {
	actualizarPreferencias,
	ENTIDADES_PROYECTADAS,
	escribirConfig,
	leerConfig,
	promesa,
	type CatalogosLocales,
	type Preferencias,
	type Sesion,
} from './bd';
import { esperasDePadre, filtrarPorDependencias, marcarResultados, pendientesPorEpoch, type ResultadoDelServer } from './cola';
import { subirFotosPendientes } from './fotos';
import { podarEntendidos } from '../nucleo/avisos';

export const MAX_POR_LOTE = 25;
const ESTADOS_VALIDOS = new Set(['aplicada', 'duplicada', 'rechazada', 'conflicto', 'pendiente']);
/**
 * Producción mira `llenados_abiertos`, que cambia con lo que hacen los OTROS
 * teléfonos: las tres encargadas se turnan cada ocho horas sobre los mismos silos.
 * Con una ventana de seis horas, la que entra no veía el silo que abrió la que salió
 * y podía cargar uno entero para que el servidor se lo rechazara por `silo_ocupado`
 * (Codex, ronda del diff, 8/9). Verde tampoco puede esperar seis horas: un lote dado
 * de alta a media mañana tiene que aparecer en «¿de qué lote viene?» con la app
 * abierta, y el sync periódico con esa pantalla a la vista no servía de nada si la
 * ventana lo saltaba (Codex, ronda del diff de la revisión, 13/9).
 */
const CATALOGOS_VIEJOS_MS = 10 * 60 * 1000;
const TIMEOUT_SYNC_MS = 60_000;

/**
 * `reiniciado` (v6, §12.7): el servidor dijo que lo de este teléfono es de un arranque anterior (un
 * reinicio de temporada); se purgó todo menos la sesión y la app tiene que volver a arrancar.
 */
export type ResultadoSync = 'ok' | 'sin_pendientes' | 'sin_sesion' | 'desenrolado' | 'error_cliente' | 'fallo_red' | 'ocupado' | 'quedan_fotos' | 'reiniciado';

/** Lo que sobrevive en `config` a la purga de un reinicio (lista blanca: una clave futura no sobrevive por olvido). */
export const CLAVES_QUE_SOBREVIVEN = new Set(['sesion', 'cola', 'arranque']);
/** Los stores que la purga vacía enteros. */
const STORES_PURGADOS = ['outbox', 'recibos', 'borradores', 'fotos', 'catalogos'] as const;

/** El build de esta PWA, que el sync reporta (v6, §12.7). */
const VERSION_APP = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';

/**
 * Un reinicio de temporada (v6, §12.7): borra en UNA transacción la cola, los recibos, los
 * borradores, las fotos pendientes, los catálogos y toda clave de `config` salvo la sesión, los
 * metadatos de la cola y el arranque, y guarda el arranque nuevo. No desenrola.
 */
export async function purgarPorArranque(bd: IDBDatabase, vigente: string): Promise<void> {
	const tx = bd.transaction(['config', ...STORES_PURGADOS], 'readwrite');
	for (const store of STORES_PURGADOS) tx.objectStore(store).clear();
	const config = tx.objectStore('config');
	const claves = (await promesa(config.getAllKeys())) as IDBValidKey[];
	for (const clave of claves) {
		if (typeof clave !== 'string' || !CLAVES_QUE_SOBREVIVEN.has(clave)) config.delete(clave);
	}
	config.put(vigente, 'arranque');
	await new Promise<void>((resolver, rechazar) => {
		tx.oncomplete = () => resolver();
		tx.onerror = () => rechazar(tx.error ?? new Error('la purga falló'));
	});
}

/** El arranque vigente de un 409 `arranque_viejo`, o null si el 409 es otra cosa. */
async function arranqueDel409(respuesta: Response): Promise<string | null> {
	if (respuesta.status !== 409) return null;
	const cuerpo = (await respuesta.json().catch(() => null)) as { error?: unknown; arranque_id?: unknown } | null;
	return cuerpo?.error === 'arranque_viejo' && typeof cuerpo.arranque_id === 'string' ? cuerpo.arranque_id : null;
}

/** Después de purgar: un sync vacío con el arranque nuevo adhiere al teléfono (sin eso, no baja catálogos). */
async function adherirse(traer: typeof fetch, sesion: Sesion, epoch: string, vigente: string): Promise<boolean> {
	try {
		const r = await traer('/api/sync', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sesion.token}` },
			body: JSON.stringify({ protocolo: 1, epoch, mutaciones: [], arranque_id: vigente, version_app: VERSION_APP }),
			signal: AbortSignal.timeout(TIMEOUT_SYNC_MS),
		});
		return r.ok;
	} catch {
		return false;
	}
}

export interface RespuestaSyncValidada {
	hora_servidor: string;
	estacion: string | null;
	/** v6 (§12.7): ausente en un servidor anterior a la v6. */
	arranque_id?: string;
	resultados: ResultadoDelServer[];
}

/** Valida el shape completo de la respuesta del server; null = malformada (no tocar nada). */
export function validarRespuestaSync(cuerpo: unknown, idsEnviados: string[]): RespuestaSyncValidada | null {
	if (typeof cuerpo !== 'object' || cuerpo === null) return null;
	const { hora_servidor, estacion, resultados, arranque_id } = cuerpo as { hora_servidor?: unknown; estacion?: unknown; resultados?: unknown; arranque_id?: unknown };
	if (arranque_id !== undefined && typeof arranque_id !== 'string') return null;
	if (typeof hora_servidor !== 'string' || Number.isNaN(Date.parse(hora_servidor))) return null;
	if (estacion !== null && estacion !== undefined && typeof estacion !== 'string') return null;
	if (!Array.isArray(resultados) || resultados.length !== idsEnviados.length) return null;
	const enviados = new Set(idsEnviados);
	const vistos = new Set<string>();
	const validados: ResultadoDelServer[] = [];
	for (const crudo of resultados) {
		if (typeof crudo !== 'object' || crudo === null) return null;
		const r = crudo as Record<string, unknown>;
		if (typeof r.id !== 'string' || !enviados.has(r.id) || vistos.has(r.id)) return null;
		if (typeof r.estado !== 'string' || !ESTADOS_VALIDOS.has(r.estado)) return null;
		if (r.raiz_id !== undefined && typeof r.raiz_id !== 'string') return null;
		if (r.procesada_en !== undefined && (typeof r.procesada_en !== 'string' || Number.isNaN(Date.parse(r.procesada_en)))) return null;
		vistos.add(r.id);
		validados.push({
			id: r.id,
			estado: r.estado as ResultadoDelServer['estado'],
			motivo: typeof r.motivo === 'string' ? r.motivo : undefined,
			nro_viaje: typeof r.nro_viaje === 'number' ? r.nro_viaje : undefined,
			nro_muestra: typeof r.nro_muestra === 'number' ? r.nro_muestra : undefined,
			numero: typeof r.numero === 'number' ? r.numero : undefined,
			raiz_id: typeof r.raiz_id === 'string' ? r.raiz_id : undefined,
			procesada_en: typeof r.procesada_en === 'string' ? r.procesada_en : undefined,
		});
	}
	return {
		hora_servidor,
		estacion: typeof estacion === 'string' ? estacion : null,
		...(typeof arranque_id === 'string' ? { arranque_id } : {}),
		resultados: validados,
	};
}

async function conLock<T>(fn: () => Promise<T>, siOcupado: T): Promise<T> {
	if (typeof navigator !== 'undefined' && navigator.locks) {
		const resultado = await navigator.locks.request('perejil-sync', { ifAvailable: true }, async (lock) => {
			if (!lock) return siOcupado;
			return fn();
		});
		return resultado as T;
	}
	return fn();
}

/** Drena la cola completa (lotes sucesivos) y refresca catálogos si están viejos. */
export interface OpcionesDeSync {
	/** Hasta cuántos ms vale el catálogo guardado; sin esto, diez minutos (v6: dos con el inicio de producción a la vista). */
	catalogosVigentesMs?: number;
}

export async function sincronizar(bd: IDBDatabase, traer: typeof fetch = fetch, opciones: OpcionesDeSync = {}): Promise<ResultadoSync> {
	return conLock<ResultadoSync>(async () => {
		const sesion = await leerConfig<Sesion>(bd, 'sesion');
		if (!sesion) return 'sin_sesion';

		let hizoAlgo = false;
		let errorCliente = false;
		let tocoProduccion = false;
		// v6 (§12.7): el arranque al que pertenece lo que tiene este teléfono. Una PWA que se actualizó
		// desde una anterior a la v6 no tiene ninguno: no lo manda y el servidor le contesta el vigente.
		let arranque = await leerConfig<string>(bd, 'arranque');
		// El loop relee los pendientes tras cada lote: un lote aplicado puede
		// dejar más filas del mismo epoch, o destapar el epoch siguiente.
		for (;;) {
			// H4 de la ronda del plan: los epochs congelados por un 401 anterior NO
			// se reenvían solos — el encargado los libera desde la config.
			const congelados = new Set(((await leerConfig<Preferencias>(bd, 'preferencias'))?.epochs_congelados ?? []) as string[]);
			const grupos = (await pendientesPorEpoch(bd)).filter((g) => !congelados.has(g.epoch));
			if (grupos.length === 0) break;
			const grupo = grupos[0];
			// Regla 14: los hijos esperan el acuse de su padre, identificado por su MUTACIÓN
			// —no por el registro, que el silo comparte con su propio cierre—. Se mira contra
			// TODA la cola, epochs congelados incluidos: un padre que no puede viajar sigue
			// bloqueando a su hijo, y eso tiene que verse (Codex, ronda del diff, 8/9).
			const todosLosPendientes = await pendientesPorEpoch(bd);
			const mutacionesPendientes = new Set(todosLosPendientes.flatMap((g) => g.filas.map((f) => f.id)));
			// Primero se elige lo enviable y DESPUÉS se corta a `MAX_POR_LOTE`: al revés, un
			// hijo bloqueado dentro de los primeros 25 dejaba lugar vacío en el lote.
			const lote = filtrarPorDependencias(grupo.filas, mutacionesPendientes).slice(0, MAX_POR_LOTE);
			if (lote.length === 0) {
				// Este grupo espera padres que no pueden viajar (un epoch congelado por un
				// 401 o un re-enrol, C1): reintentar sería un bucle. Se corta. Quien carga
				// avisa a gestión (una sola persona); ella reenvía desde la configuración
				// del teléfono, después de mirar la oficina.
				console.error('sync: hay pendientes esperando un padre que no puede viajar');
				const todas = todosLosPendientes.flatMap((g) => g.filas);
				const traba = esperasDePadre(todas, congelados).some((e) => e.padreCongelado);
				await actualizarPreferencias(bd, {
					ultimo_error_sync: traba
						? 'Hay registros de un enrolamiento anterior. Avisale a gestión.'
						: 'Hay registros esperando a otro que no pudo subir. Avisale a gestión.',
				});
				break;
			}
			const cuerpo = {
				protocolo: 1,
				epoch: grupo.epoch,
				mutaciones: lote.map((f) => ({ id: f.id, seq: f.seq, tipo: f.tipo, payload: f.payload, creado_en: f.creado_en })),
				...(arranque ? { arranque_id: arranque } : {}),
				version_app: VERSION_APP,
			};

			const t0 = Date.now();
			let respuesta: Response;
			try {
				respuesta = await traer('/api/sync', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sesion.token}` },
					body: JSON.stringify(cuerpo),
					// Sin timeout, un portal cautivo que no cierra la conexión dejaría
					// el Web Lock tomado y a las otras pestañas en 'ocupado' para siempre.
					signal: AbortSignal.timeout(TIMEOUT_SYNC_MS),
				});
			} catch {
				return 'fallo_red';
			}
			const t1 = Date.now();

			if (respuesta.status === 401) {
				return congelarPorTokenMuerto(bd);
			}
			const vigente = await arranqueDel409(respuesta);
			if (vigente !== null) return reiniciar(bd, traer, sesion, vigente);
			// 411 (sin Content-Length) y 413 (lote por encima del techo) son tan permanentes
			// como el 400: el navegador siempre declara el largo, y un lote no se achica solo.
			if (respuesta.status === 400 || respuesta.status === 411 || respuesta.status === 413) {
				const detalle = await respuesta.text().catch(() => '');
				console.error('sync: el server rechazó el sobre (bug de cliente)', respuesta.status, detalle);
				await actualizarPreferencias(bd, { ultimo_error_sync: `El servidor rechazó el envío (${respuesta.status}). ${detalle}`.slice(0, 300) });
				// `break`, no `return`: un 400 es permanente hasta que se deploye un
				// fix, y con `return` los catálogos no se refrescarían nunca.
				errorCliente = true;
				break;
			}
			if (!respuesta.ok) return 'fallo_red';

			const validada = validarRespuestaSync(
				await respuesta.json().catch(() => null),
				lote.map((f) => f.id),
			);
			if (!validada) return 'fallo_red'; // respuesta malformada: no tocar nada, reintentar

			await marcarResultados(bd, validada.resultados, validada.hora_servidor);
			await adoptarEstacion(bd, sesion, validada.estacion);
			if (validada.arranque_id !== undefined && validada.arranque_id !== arranque) {
				await escribirConfig(bd, 'arranque', validada.arranque_id);
				arranque = validada.arranque_id;
			}
			// Un reinicio entró en el medio del lote (el batch lo decidió): la próxima pasada recibe el 409.
			if (validada.resultados.some((r) => r.motivo === 'arranque_cambiado')) break;
			// pendiente en crear es una violación de contrato del server: visible.
			if (validada.resultados.some((r) => r.estado === 'pendiente')) {
				console.error('sync: el server respondió pendiente a una creación');
				await actualizarPreferencias(bd, { ultimo_error_sync: 'El servidor dejó pendiente una creación (anomalía de contrato).' });
				break; // no re-loopear con el mismo lote infinito
			}
			await actualizarPreferencias(bd, {
				desvio_ms: Date.parse(validada.hora_servidor) - (t0 + t1) / 2,
				hora_ultimo_sync: validada.hora_servidor,
				desenrolado: false,
				ultimo_error_sync: undefined,
			});
			hizoAlgo = true;
			// Producción se mira contra `llenados_abiertos` y calidad contra
			// `llenados_para_muestra`, que vienen en los catálogos: si acaba de entrar un silo
			// o una muestra, la pantalla tiene que verlo YA y no dentro de seis horas.
			// Mientras tanto la proyección se apoya en los recibos.
			if (lote.some((f) => ENTIDADES_PROYECTADAS.has(f.entidad))) tocoProduccion = true;
		}

		// Las fotos van DESPUÉS del outbox y dentro del mismo lock: un silo con acuse en
		// esta pasada sube su evidencia en esta misma pasada (§5.3).
		const fotos = await subirFotosPendientes(bd, sesion, traer);
		if (fotos.tokenMuerto) return congelarPorTokenMuerto(bd);

		// Sin nada que subir, la pasada no llegó a hablar con el servidor: un teléfono que todavía no
		// tiene arranque guardado (actualizado desde una PWA anterior a la v6) lo pide con un lote vacío.
		if (!arranque && !hizoAlgo) {
			const vacio = await sincronizarVacio(bd, traer, sesion);
			if (vacio === 'reiniciado' || vacio === 'desenrolado') return vacio;
		}

		const catalogos = await refrescarCatalogosSiViejos(bd, traer, sesion, tocoProduccion, opciones.catalogosVigentesMs);
		if (catalogos !== null) return reiniciar(bd, traer, sesion, catalogos);

		// El estado que MANDA es el que necesita reintento: un 400 del sobre ya quedó en
		// `ultimo_error_sync`; al revés, `error_cliente` se comería la reprogramación de las fotos.
		if (fotos.transitorio) return 'fallo_red';
		if (fotos.quedan > 0) return 'quedan_fotos';
		if (errorCliente) return 'error_cliente';
		return hizoAlgo || fotos.subidas > 0 || fotos.falladas > 0 ? 'ok' : 'sin_pendientes';
	}, 'ocupado');
}

/**
 * La estación vigente la manda el servidor en cada respuesta (§5.5): si gestión
 * la cambió, la sesión local se realinea sin re-enrolar. La PWA cambia de flujo
 * en el próximo arranque.
 */
async function adoptarEstacion(bd: IDBDatabase, sesion: Sesion, estacion: string | null): Promise<void> {
	if (estacion === sesion.estacion) return;
	sesion.estacion = estacion;
	await escribirConfig(bd, 'sesion', sesion);
}

/**
 * Token muerto: congela TODO lo pendiente (epochs incluidos). Tras re-enrolar,
 * la cola vieja se reenvía solo por decisión del encargado — lo ya aplicado sin
 * ACK aparecería como conflicto id_reusado (§5.4).
 */
async function congelarPorTokenMuerto(bd: IDBDatabase): Promise<ResultadoSync> {
	const todos = await pendientesPorEpoch(bd);
	await actualizarPreferencias(bd, { desenrolado: true, epochs_congelados: todos.map((g) => g.epoch) });
	return 'desenrolado';
}

/** Purga, se adhiere al arranque nuevo y le pide a la app que vuelva a arrancar (v6, §12.7). */
async function reiniciar(bd: IDBDatabase, traer: typeof fetch, sesion: Sesion, vigente: string): Promise<ResultadoSync> {
	await purgarPorArranque(bd, vigente);
	const cola = await leerConfig<{ epoch: string }>(bd, 'cola');
	if (cola && (await adherirse(traer, sesion, cola.epoch, vigente))) {
		await refrescarCatalogosSiViejos(bd, traer, sesion, true);
	} else {
		await actualizarPreferencias(bd, { catalogos_por_refrescar: true });
	}
	return 'reiniciado';
}

/** Un lote vacío, para saber el arranque vigente (y el de la respuesta queda guardado). */
async function sincronizarVacio(bd: IDBDatabase, traer: typeof fetch, sesion: Sesion): Promise<ResultadoSync | null> {
	const cola = await leerConfig<{ epoch: string }>(bd, 'cola');
	if (!cola) return null;
	let respuesta: Response;
	try {
		respuesta = await traer('/api/sync', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sesion.token}` },
			body: JSON.stringify({ protocolo: 1, epoch: cola.epoch, mutaciones: [], version_app: VERSION_APP }),
			signal: AbortSignal.timeout(TIMEOUT_SYNC_MS),
		});
	} catch {
		return null;
	}
	if (respuesta.status === 401) return congelarPorTokenMuerto(bd);
	const vigente = await arranqueDel409(respuesta);
	if (vigente !== null) return reiniciar(bd, traer, sesion, vigente);
	if (!respuesta.ok) return null;
	const validada = validarRespuestaSync(await respuesta.json().catch(() => null), []);
	if (validada?.arranque_id !== undefined) await escribirConfig(bd, 'arranque', validada.arranque_id);
	return null;
}

/**
 * Baja los catálogos si los locales tienen más de diez minutos, o siempre con `forzar`. Devuelve
 * el arranque vigente si el servidor dijo que este teléfono es de uno anterior (v6, §12.7); si no, null.
 */
export async function refrescarCatalogosSiViejos(
	bd: IDBDatabase,
	traer: typeof fetch,
	sesion: Sesion,
	forzar = false,
	vigentesMs: number = CATALOGOS_VIEJOS_MS,
): Promise<string | null> {
	const preferencias = (await leerConfig<Preferencias>(bd, 'preferencias')) ?? {};
	const edad = Date.now() - (preferencias.catalogos_bajados_en ?? 0);
	// Un refresco forzado que falló se debe: hasta que entre, la pantalla vive de los recibos, y una
	// pasada siguiente sin nada que subir no lo pediría sola (Codex, ronda de la v5 entera, 15/9).
	const debido = forzar || preferencias.catalogos_por_refrescar === true;
	if (!debido && edad < vigentesMs) return null;
	let entro = false;
	let viejo: string | null = null;
	try {
		const t0 = Date.now();
		const respuesta = await traer('/api/catalogos', {
			// 20/10 (§5.5): el build viaja también en el GET, para que un teléfono que solo mira lo reporte.
			headers: { Authorization: `Bearer ${sesion.token}`, 'X-Version-App': VERSION_APP },
			// Con timeout: sin él, un portal cautivo colgaría el Web Lock acá mismo,
			// después de que el resto de la pasada ya se dio por vencido.
			signal: AbortSignal.timeout(TIMEOUT_SYNC_MS),
		});
		const t1 = Date.now();
		viejo = await arranqueDel409(respuesta);
		if (respuesta.ok) {
			const cuerpo = (await respuesta.json()) as { catalogos?: CatalogosLocales; estacion?: string | null; hora_servidor?: string };
			if (cuerpo?.catalogos) {
				const horaServidor = typeof cuerpo.hora_servidor === 'string' ? cuerpo.hora_servidor : undefined;
				// El GET también calibra el reloj, con la fórmula del POST: un teléfono que solo mira (verde
				// reciente, §5.5) no manda nada y sin esto no recalibraba nunca (Codex, cuarta ronda, 5/10).
				const desvioMs = horaServidor === undefined ? undefined : Date.parse(horaServidor) - (t0 + t1) / 2;
				// En UNA transacción: el snapshot y la marca con la que se decide qué efectos ya
				// contiene tienen que entrar o no entrar juntos. Separados, una app que muere en
				// el medio se queda con el catálogo nuevo y la marca vieja, y vuelve a aplicarle
				// encima efectos que ya trae (Codex, ronda del diff, 8/9).
				await escribirConfigCatalogos(bd, cuerpo.catalogos, horaServidor, desvioMs !== undefined && Number.isFinite(desvioMs) ? desvioMs : undefined);
				if (cuerpo.estacion !== undefined) await adoptarEstacion(bd, sesion, cuerpo.estacion);
				entro = true;
			}
		}
	} catch {
		// sin señal: los catálogos cacheados siguen valiendo
	}
	if (!entro && debido && preferencias.catalogos_por_refrescar !== true) await actualizarPreferencias(bd, { catalogos_por_refrescar: true });
	return viejo;
}

export async function escribirConfigCatalogos(bd: IDBDatabase, catalogos: CatalogosLocales, horaServidor?: string, desvioMs?: number): Promise<void> {
	const tx = bd.transaction(['catalogos', 'config'], 'readwrite');
	tx.objectStore('catalogos').put(catalogos, 'catalogos');
	const config = tx.objectStore('config');
	// v6 (§12.2): los «Entendido» de avisos que ya no vienen (anulados o vencidos) se podan en la
	// misma transacción que el catálogo nuevo.
	const entendidos = (await promesa(config.get('avisos_entendidos'))) as string[] | undefined;
	if (entendidos !== undefined && catalogos.avisos_turno !== undefined) config.put(podarEntendidos(entendidos, catalogos.avisos_turno), 'avisos_entendidos');
	const previas = ((await promesa(config.get('preferencias'))) as Preferencias | undefined) ?? {};
	config.put(
		{
			...previas,
			catalogos_bajados_en: Date.now(),
			catalogos_por_refrescar: false,
			...(horaServidor === undefined ? {} : { catalogos_hora_servidor: horaServidor }),
			...(desvioMs === undefined ? {} : { desvio_ms: desvioMs }),
		},
		'preferencias',
	);
	await new Promise<void>((resolver, rechazar) => {
		tx.oncomplete = () => resolver();
		tx.onerror = () => rechazar(tx.error ?? new Error('la transacción falló'));
	});
}

export async function leerCatalogos(bd: IDBDatabase): Promise<CatalogosLocales | undefined> {
	const tx = bd.transaction('catalogos');
	const pedido = tx.objectStore('catalogos').get('catalogos') as IDBRequest<CatalogosLocales | undefined>;
	return new Promise((resolver, rechazar) => {
		pedido.onsuccess = () => resolver(pedido.result);
		pedido.onerror = () => rechazar(pedido.error ?? new Error('fallo de IndexedDB'));
	});
}

/**
 * Planificador con backoff exponencial + jitter (30s → tope 30m) ante fallo.
 * Un disparo durante una corrida NO se pierde (A6 de la ronda del diff): queda
 * marcado y provoca otra pasada al terminar; `ocupado` (lock de otra pestaña)
 * reintenta corto.
 */
export function crearPlanificador(correr: () => Promise<ResultadoSync>): { disparar: () => void; detener: () => void } {
	let intentosFallidos = 0;
	let timer: ReturnType<typeof setTimeout> | null = null;
	let corriendo = false;
	let volverACorrer = false;

	const programar = (ms: number) => {
		if (timer) clearTimeout(timer);
		timer = setTimeout(() => void ejecutar(), ms);
	};

	const ejecutar = async (): Promise<void> => {
		if (corriendo) {
			volverACorrer = true;
			return;
		}
		corriendo = true;
		try {
			// Una excepción inesperada (cuota de IndexedDB, por ejemplo) no puede
			// dejar al planificador sin reprogramar: se trata como fallo transitorio.
			const resultado = await correr().catch((error): ResultadoSync => {
				console.error('sync: la pasada falló de forma inesperada', error);
				return 'fallo_red';
			});
			if (resultado === 'fallo_red') {
				intentosFallidos += 1;
				const baseMs = Math.min(30_000 * 2 ** (intentosFallidos - 1), 30 * 60_000);
				programar(baseMs * (0.75 + Math.random() * 0.5));
			} else if (resultado === 'ocupado') {
				programar(2_000); // otra pestaña tiene el lock: reintento corto
			} else if (resultado === 'quedan_fotos') {
				intentosFallidos = 0;
				programar(3_000); // el tope de la pasada dejó fotos: la siguiente las lleva
			} else {
				intentosFallidos = 0;
			}
		} finally {
			corriendo = false;
			if (volverACorrer) {
				volverACorrer = false;
				void ejecutar();
			}
		}
	};

	return {
		disparar: () => {
			intentosFallidos = 0;
			if (timer) clearTimeout(timer);
			void ejecutar();
		},
		detener: () => {
			if (timer) clearTimeout(timer);
		},
	};
}
